import { getLatestAlignmentSuggestionVersion } from '@/lib/ai-session/capabilities/alignment/alignment-suggestion-version';
import { hasPromptableReadingSkillRecommendation } from '@/lib/ai-session/capabilities/alignment/alignment-session-payload';
import type { CanonicalAISession } from '@/lib/ai-session/contracts/types';
import { getBusinessErrorCode } from '@/lib/error-utils';
import { AISessionStreamError } from './stream-events/run-lifecycle';

export const AI_SESSION_STREAM_UNFINISHED_MESSAGE = 'AI 回复未正常结束，请重试';
export const AI_SESSION_STREAM_UNFINISHED_CODE = 'AI_SESSION_STREAM_UNFINISHED';

export function createUnfinishedAiSessionStreamError(cause?: unknown): AISessionStreamError {
  return new AISessionStreamError(AI_SESSION_STREAM_UNFINISHED_MESSAGE, {
    errorCode: AI_SESSION_STREAM_UNFINISHED_CODE,
    recoverable: true,
    cause,
  });
}

export const MAX_ACTIVE_AI_SESSION_RUN_RECONNECT = 8;

const ACTIVE_AI_SESSION_RUN_STATUSES = new Set(['pending', 'running', 'classifying']);

export function isUnfinishedAiSessionStreamError(error: unknown): boolean {
  return getBusinessErrorCode(error) === AI_SESSION_STREAM_UNFINISHED_CODE;
}

/** SSE 短连时：Run 仍在执行则继续重连，不得按「未正常结束」收口。 */
export function isActiveAiSessionRunStatus(status: string | null | undefined): boolean {
  return typeof status === 'string' && ACTIVE_AI_SESSION_RUN_STATUSES.has(status);
}

export function shouldContinueActiveAiSessionReconnect(continues: number): boolean {
  return (
    Number.isInteger(continues) && continues >= 0 && continues < MAX_ACTIVE_AI_SESSION_RUN_RECONNECT
  );
}

/**
 * SSE 断流但服务端已停在「可提示选技能」时，应按成功收口，避免假失败重试。
 */
export function shouldRecoverAlignmentStreamUnfinishedAsSuccess(
  session: CanonicalAISession | null | undefined
): boolean {
  if (!session) return false;
  return hasPromptableReadingSkillRecommendation(session);
}

/** 确认修订 SSE 断流，但拆解建议版本已经增长：按成功收口，避免 V2 已落地仍报失败。 */
export function shouldRecoverAlignmentConfirmUnfinishedAsSuccess(
  priorVersion: number | null,
  session: CanonicalAISession | null | undefined
): boolean {
  if (!session) return false;
  const nextVersion = getLatestAlignmentSuggestionVersion(session);
  if (nextVersion == null || priorVersion == null) return false;
  return nextVersion > priorVersion;
}
